<template>
  <div>
    <GNavBar></GNavBar>
    <h1 class="mt-5 pt-5">IPA payment information</h1>
    <div v-if="loading">
      <LoadingScreen></LoadingScreen>
    </div>
    <b-row v-else align-h="center">
      <b-col md="12">
        <Announcements
          v-if="announcements.length > 0"
          class="mb-3"
          :annArray="announcements"
        ></Announcements>
        <b-card title="Summary" class="mb-3">
          <div class="d-flex flex-column pb-3">
            <span
              >Number of IPAs to be paid:
              <strong>{{ contractsToBePaid.length }}</strong></span
            >
            <span
              >Number of projects currently in review:
              <strong>{{ 0 }}</strong></span
            >
            <span>
              Link to payment documents:
              <b-link
                href="https://www.dropbox.com/sh/cfpa1om2luigyqf/AAB2Uy0MeetVOXzRSF8U3vxia?dl=0"
                target="_blank"
                >🗂</b-link
              >
            </span>
            <span>
              Link to upload developer invoices:
              <b-link
                href="https://www.dropbox.com/request/OdXzIQkGvf1qhnGPDCbn"
                target="_blank"
                >📥</b-link
              >
            </span>
          </div>
          <div class="d-md-flex align-items-baseline justify-content-between">
            <b-form-group class="w-50" label-for="search-input">
              <b-input-group>
                <b-form-input
                  @update="searchInputHandler"
                  type="search"
                  id="search-input"
                  placeholder="Search by course or developer last name"
                ></b-form-input>
                <b-input-group-append>
                  <b-button :disabled="!searchTerm" @click="searchTerm = ''"
                    >Clear</b-button
                  >
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <b-form-checkbox
              id="includeCancelledSections"
              v-model="includeSentForPayment"
            >
              Include IPAs recently SfP
            </b-form-checkbox>
          </div>
        </b-card>
        <div>
          <b-card no-body>
            <b-tabs pills card>
              <b-tab title="IPAs for completed projects" active>
                <vue-json-to-csv
                  :json-data="contracts"
                  :csv-title="'ipas_to_be_paid.csv'"
                >
                  <b-button>
                    <strong>Download all data</strong>
                  </b-button>
                </vue-json-to-csv>
                <b-card
                  class="mx-5 mt-3"
                  v-if="!contractsToShow"
                  border-variant="warning"
                  header="Notice"
                >
                  No contracts to show. 🙈
                </b-card>
                <b-list-group v-else flush>
                  <b-list-group-item
                    v-for="contract in contractsToShow"
                    :key="contract.id"
                  >
                    <div>
                      <h5>
                        {{ contract.project_id }}: {{ contract.course }} ({{
                          contract.last_name
                        }})
                      </h5>
                      <ContractInfo
                        class="pb-2"
                        :getData="getData"
                        :contract="contract"
                      />
                    </div>
                    <b-link
                      :href="
                        `https://ofditprojects.sps.cuny.edu/projects/${contract.project_id}`
                      "
                      class="btn btn-primary float-right"
                      target="_blank"
                      >Go to project</b-link
                    >
                  </b-list-group-item>
                </b-list-group>
              </b-tab>
              <b-tab title="Projects in review">
                <b-card
                  class="mx-5 mt-3"
                  border-variant="warning"
                  header="Notice"
                >
                  No contracts to show. 🙈
                </b-card>
              </b-tab>
            </b-tabs>
          </b-card>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import GNavBar from '@/components/GNavBar.vue'
  import LoadingScreen from '@/components/LoadingScreen'
  import Announcements from '@/components/Announcements'
  import ContractInfo from '@/components/ContractInfo'
  import VueJsonToCsv from 'vue-json-to-csv'
  import Fuse from 'fuse.js'

  export default {
    name: 'PaymentInfoPage',
    async created() {
      await this.checkAuthorization()
      await this.$store.dispatch('setup')
    },
    data() {
      return {
        contractsObj: null,
        includeSentForPayment: false,
        searchTerm: null,
      }
    },
    computed: {
      currentUser() {
        return this.$store.getters.currentUser
      },
      loading() {
        return this.$store.getters.loadingStatus
      },
      announcements() {
        return this.$store.getters.announcements.filter(a => a.module === 'pay')
      },
      contracts() {
        return this.contractsObj.result
      },
      contractsToBePaid() {
        return this.contracts.filter(c => c.status !== 'Sent for payment')
      },
      contractsToShow() {
        if (this.includeSentForPayment) {
          if (!this.searchTerm) {
            return this.contracts
          } else {
            return this.searchContracts(this.contracts)
          }
        } else {
          if (!this.searchTerm) {
            return this.contractsToBePaid
          } else {
            return this.searchContracts(this.contractsToBePaid)
          }
        }
      },
    },
    methods: {
      searchContracts(contracts) {
        const options = {
          minMatchCharLength: 3,
          threshold: 0.5,
          keys: ['course', 'last_name'],
        }
        let result
        const fuse = new Fuse(contracts, options)
        result = fuse.search(this.searchTerm)
        return result.reduce((acc, curr) => {
          acc.push(curr.item)
          return acc
        }, [])
      },
      async checkAuthorization() {
        if (
          this.currentUser.program !== 'OFDIT' &&
          this.currentUser.program !== 'ADMIN'
        ) {
          this.$router.push({
            name: 'error',
            params: {
              title: 'Not authorized',
              message:
                'You are not currently authorized to access this resource. If you believe this is an error, please contact OFDIT.',
              errObj: this.currentUser,
            },
          })
        } else {
          await this.getData()
        }
      },
      async getData() {
        try {
          this.contractsObj = await this.$store.dispatch('cd_callAPI', {
            url: `/d/payments`,
          })
        } catch (err) {
          this.$router.push({
            name: 'error',
            params: {
              title: 'Networking issue',
              message: err.message,
              errObj: this.$store.getters.currentUser,
            },
          })
        } finally {
          this.$store.commit('updateLoadingStatus', false)
        }
      },
      searchInputHandler(val) {
        clearTimeout(this.$_timeout)
        this.$_timeout = setTimeout(() => {
          this.searchTerm = val
        }, 150)
      },
    },
    components: {
      GNavBar,
      LoadingScreen,
      Announcements,
      ContractInfo,
      VueJsonToCsv,
    },
  }
</script>
